import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, ChevronDown, ChevronUp, MapPin, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useBrand } from '@/config/brands';
import { scenarioStorage, LocalScenario } from '@/lib/scenarioStorage';
import { familyStorage, FamilyMember } from '@/lib/familyStorage';

interface FamilyHospitalOptimizerProps {
  scenarios: LocalScenario[];
  members: FamilyMember[];
  onRefresh: () => void;
}

export function FamilyHospitalOptimizer({ scenarios, members, onRefresh }: FamilyHospitalOptimizerProps) {
  const { t } = useTranslation('family');
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const [expanded, setExpanded] = useState(false);

  // NOTE: family plans are custom/local scenarios with no catalog scenario id.
  // useHospitalPricing's RPC only prices catalog scenarios (it ignores raw
  // procedure lists), so calling it here always matched nothing and the "best
  // combo" list could never render. Be honest about the limitation instead of
  // silently showing nothing (or worse, "No hospitals found").

  const scenariosWithHospitals = scenarios.filter(s => s.preferredHospitalId);
  const scenariosWithoutHospitals = scenarios.filter(s => !s.preferredHospitalId);

  // Group scenarios by preferred hospital
  const hospitalGroups = useMemo(() => {
    const groups: Record<string, { name: string; scenarios: LocalScenario[]; totalCost: number }> = {};
    for (const s of scenariosWithHospitals) {
      const hid = s.preferredHospitalId!;
      if (!groups[hid]) {
        groups[hid] = { name: s.preferredHospitalName || 'Unknown', scenarios: [], totalCost: 0 };
      }
      groups[hid].scenarios.push(s);
      groups[hid].totalCost += s.hospitalEstimatedCost || s.estimatedCost || 0;
    }
    return groups;
  }, [scenariosWithHospitals]);

  if (scenarios.length === 0) return null;

  const handleAssignHospital = (scenarioId: string, hospitalId: string, hospitalName: string, estimatedCost: number) => {
    scenarioStorage.updateScenario(scenarioId, {
      preferredHospitalId: hospitalId,
      preferredHospitalName: hospitalName,
      hospitalEstimatedCost: estimatedCost,
    });
    onRefresh();
  };

  return (
    <div className={`rounded-xl border p-4 ${
      isAhp ? 'border-gray-200 bg-white' : 'border-foreground/10 bg-card'
    }`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center justify-between w-full"
      >
        <div className="flex items-center gap-2">
          <Building2 className={`w-4 h-4 ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`} />
          <h2 className={`text-base font-semibold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
            {t('optimizer.title')}
          </h2>
          {scenariosWithHospitals.length > 0 && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${
              isAhp ? 'bg-green-100 text-green-700' : 'bg-matrix-green/20 text-matrix-green'
            }`}>
              {scenariosWithHospitals.length}/{scenarios.length} {t('optimizer.assigned', 'assigned')}
            </span>
          )}
        </div>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Hospital groups */}
          {Object.entries(hospitalGroups).length > 0 && (
            <div className="space-y-2">
              <p className={`text-xs font-medium ${isAhp ? 'text-gray-500' : 'text-foreground/50'}`}>
                {t('optimizer.assignedHospitals', 'Assigned Hospitals')}
              </p>
              {Object.entries(hospitalGroups).map(([hid, group]) => (
                <div key={hid} className={`p-3 rounded-lg border ${
                  isAhp ? 'border-green-200 bg-green-50/50' : 'border-matrix-green/20 bg-matrix-green/5'
                }`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className={`text-sm font-semibold ${isAhp ? 'text-gray-900' : 'text-foreground'}`}>
                      {group.name}
                    </p>
                    <span className={`text-sm font-mono font-bold ${isAhp ? 'text-civic-navy' : 'text-matrix-green'}`}>
                      ${group.totalCost.toLocaleString()}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {group.scenarios.map(s => {
                      const member = members.find(m => m.id === s.familyMemberId);
                      return (
                        <p key={s.id} className="text-[10px] text-muted-foreground">
                          {s.name}{member ? ` (${familyStorage.getDisplayName(member)})` : ''}
                          {' — '}
                          <span className="font-mono">${(s.hospitalEstimatedCost || s.estimatedCost || 0).toLocaleString()}</span>
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Automatic price comparison only works for catalog scenarios (custom/
              family plans have no scenario_hospital_coverage rows). State that
              plainly rather than pretending we searched and found nothing. */}
          <p className="text-xs text-center text-muted-foreground py-4">
            {t('optimizer.catalogOnly', 'Hospital price comparison is available for catalog scenarios — open a scenario page to compare hospitals near you.')}
          </p>
        </div>
      )}
    </div>
  );
}
